import type { Controller } from "@ctrl/controller-rpc";

import type { Router } from "@/types";
import {
  internalPathFromRoutineId,
  isRoutinePrefixedId,
  referenceIdFromString,
} from "@/utils/hyperlink";
import {
  navigateToInternalPathOrObject,
  navigateToObject,
} from "@/utils/navigate";
import { open } from "@/utils/open";

const notifyCouldNotOpen = (controller: Controller | null) => {
  if (controller) {
    controller.notifications.notify("Couldn't open this link");
  }
};

/**
 * Open a link.
 *
 * - If the string is a Routine record id (`event:…`, `object:…`,
 *   `automation:…`, …) and a `router` is provided, navigate inside the app
 *   instead of handing it to the OS. Destination routes render Not found
 *   when the id can no longer be resolved.
 * - Routine ids are never handed to the OS, even without a router: macOS
 *   otherwise shows "There is no application set to open the URL …".
 * - Otherwise, hand the URL to the OS. Failures (e.g. unsupported protocol)
 *   are caught and surfaced as a non-fatal notification rather than letting
 *   the underlying `Promise` rejection escape and trigger the global fatal
 *   error modal.
 */
export const openExternalUrl = async (
  link: string,
  controller: Controller | null,
  notify = true,
  router: Router | null = null
) => {
  const reference = referenceIdFromString(link);
  const idPath = internalPathFromRoutineId(link);
  const routineId = Boolean(reference || idPath || isRoutinePrefixedId(link));

  if (routineId) {
    if (router && controller) {
      if (reference) {
        await navigateToObject(router, controller, reference);
        return;
      }
      await navigateToInternalPathOrObject(controller, router, idPath ?? link);
      return;
    }
    // Never hand Routine ids to the OS: macOS shows "There is no
    // application set to open the URL …" for schemes like `automation:`.
    notifyCouldNotOpen(controller);
    return;
  }

  if (notify && controller) {
    await controller.notifications.notify(
      "Link opened in your default browser"
    );
  }
  try {
    await open(link);
  } catch (e) {
    window.console.warn("openExternalUrl failed", { link, error: e });
    notifyCouldNotOpen(controller);
  }
};
